import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { tuiPure, TuiStringHandler } from '@taiga-ui/cdk';
import {
  TuiAlertService,
  TuiBreakpointService,
  TuiDialogContext,
  TuiDialogService,
} from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT, PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { concatMap, map, tap } from 'rxjs';

import { BrandingService } from '@branding/branding.service';
import {
  normalizeObjectCardSettings,
  OBJECT_CARD_GENERAL_FIELDS,
  OBJECT_CARD_KINDS,
  ObjectCardBlockKey,
  ObjectCardKind,
  ObjectCardSettingsType,
} from '@common/components/object-card';
import { ErrorService } from '@common/services/error.service';
import { AppLanguage, getLanguageSelectOptions } from '@common/utils/lang';
import {
  normalizeBackgroundOpacity,
  normalizeMapObjectSettings,
} from '@common/utils/map-object-settings';
import { timezoneUTCAvailable } from '@common/utils/timezone';
import { UsersService } from '@services/users.service';
import { getCurrentUser } from '@store/actions/current_user.actions';

import { environment } from '../../../environments/environment';
import { monitoringTabs } from '../../utils/consts/monitoring-tabs-config';

import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { StringifyService } from '@common/services/stringify.service';

/** Строка настройки карточки объекта: видимость блока/поля отдельно для каждого типа карточки */
interface ObjectCardSettingRow {
  name: string;
  controls: Record<ObjectCardKind, FormControl<boolean>>;
}

function createObjectCardSettingRow(name: string): ObjectCardSettingRow {
  return {
    name,
    controls: {
      floating: new FormControl(true, { nonNullable: true }),
      fixed: new FormControl(true, { nonNullable: true }),
    },
  };
}
export type SensorsPrecisionType = 0 | 1 | 2;
interface SensorsPrecision {
  id: SensorsPrecisionType;
  title: string;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.less'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SettingsComponent implements OnInit {
  protected readonly translate = inject(TranslateService);
  protected readonly stringifyService = inject(StringifyService);
  private readonly dialogService = inject<TuiDialogService>(TuiDialogService);
  private readonly injector = inject<Injector>(Injector);
  private readonly context = inject<TuiDialogContext<any, any>>(POLYMORPHEUS_CONTEXT);
  private readonly alerts = inject<TuiAlertService>(TuiAlertService);
  private readonly userService = inject(UsersService);
  private readonly errorHandler = inject(ErrorService);
  private readonly brandingService = inject(BrandingService);
  private readonly store = inject(Store);

  /** Телефон: перетаскивание за ручку справа, а не за всю строку. Граница — 'mobile' у Taiga. */
  protected readonly isMobile = toSignal(
    inject(TuiBreakpointService).pipe(map((breakpoint) => breakpoint === 'mobile')),
    { initialValue: false },
  );

  @ViewChild('addressFormatList', { read: ElementRef })
  private readonly addressFormatList?: ElementRef<HTMLElement>;

  @ViewChild('objectCardList', { read: ElementRef })
  private readonly objectCardList?: ElementRef<HTMLElement>;

  settingsTabs = ['General', 'Additional'];
  versionInfo = this.brandingService.versionInfo;

  availableAddressFormat: any = [
    { name: 'country', control: new FormControl(true) }, // Country -> Страна
    { name: 'district', control: new FormControl(true) }, // District -> Округ
    { name: 'state', control: new FormControl(true) }, // State -> Регион
    { name: 'state_district', control: new FormControl(true) }, // State district -> Район региона
    { name: 'city', control: new FormControl(true) }, // City -> Город
    { name: 'village', control: new FormControl(true) }, // Village -> Населенный пункт
    { name: 'city_district', control: new FormControl(true) }, // City district -> Район города
    { name: 'road', control: new FormControl(true) }, // Road -> Улица
    { name: 'house_number', control: new FormControl(true) }, // House number -> Дом
  ];

  /**
   * Поля блока «Основная информация» (general.content). Порядок фиксирован и не перетаскивается,
   * блок всегда стоит первым в карточке.
   */
  availableGeneralContent: ObjectCardSettingRow[] = OBJECT_CARD_GENERAL_FIELDS.map(
    createObjectCardSettingRow,
  );

  /**
   * Остальные блоки карточки — их порядок и видимость настраиваются пользователем.
   * Порядок один на оба типа карточки: в интерфейсе один список с двумя колонками чекбоксов.
   */
  availableSettingsObjectCard: ObjectCardSettingRow[] = [
    'connectionSettings', // Настройки подключения
    'drivers', // Назначенные водители
    'trailers', // Назначенные прицепы
    'customFields', // Настраиваемые поля
    'sensors', // Значения датчиков
    'params', // Параметры
    'geozones', // Геозоны
    'specifications', // Характеристики
    'maintenance', // Техобслуживание
  ].map(createObjectCardSettingRow);

  currentTab: string = this.settingsTabs[0];

  currentProjectIsMonitoring: boolean = environment.systemType === 'monitoring';

  /** Типы карточек, настраиваемые в форме: фиксированная живёт только в списке мониторинга */
  objectCardKinds: readonly ObjectCardKind[] = this.currentProjectIsMonitoring
    ? OBJECT_CARD_KINDS
    : ['floating'];

  get user() {
    return this.context.data?.user;
  }

  get access_edit_credentials() {
    return this.user.commonAccesses.includes('access_edit_credentials');
  }

  availableLanguages = getLanguageSelectOptions();

  availableTimezones: any = timezoneUTCAvailable();

  formGroup: FormGroup = new FormGroup({
    username: new FormControl(
      {
        value: this.user?.username || '',
        disabled: !this.access_edit_credentials,
      },
      [Validators.required, Validators.maxLength(150)],
    ),
    fullname: new FormControl(this.user?.name || '', [
      Validators.required,
      Validators.maxLength(150),
    ]),
    email: new FormControl(this.user?.email || '', [Validators.required, Validators.email]),
    language: new FormControl<AppLanguage>(this.user?.language || this.availableLanguages[0].id),
    timezone: new FormControl<number>(this.user?.timezone || 0),
    addressFormat: new FormControl(this.user?.addressFormat || ''),
    visibleTabsNames: new FormControl(this.user?.visibleTabsNames || ['monitoring']),
    backgroundOpacity: new FormControl<number>(
      normalizeMapObjectSettings(this.user?.mapObjectSettings).backgroundOpacity,
    ),
  });

  sensorsSettingsForm: FormGroup = new FormGroup({
    fuel: new FormControl<SensorsPrecisionType>(
      this.user?.sensorPrecisionConfig.fuel,
      Validators.required,
    ),
    engine: new FormControl<SensorsPrecisionType>(
      this.user?.sensorPrecisionConfig.engine,
      Validators.required,
    ),
    mileage: new FormControl<SensorsPrecisionType>(
      this.user?.sensorPrecisionConfig.mileage,
      Validators.required,
    ),
    indicator: new FormControl<SensorsPrecisionType>(
      this.user?.sensorPrecisionConfig.indicator,
      Validators.required,
    ),
    arbitrarySensor: new FormControl<SensorsPrecisionType>(
      this.user?.sensorPrecisionConfig.arbitrarySensor,
      Validators.required,
    ),
  });

  protected readonly sensorControlNames = Object.keys(this.sensorsSettingsForm.controls);

  protected readonly sensorsPrecision: SensorsPrecision[] = [
    { id: 2, title: 'Leave 2 decimal places' },
    { id: 1, title: 'Trim the fractional part' },
    { id: 0, title: 'Round to the nearest whole number' },
  ];

  availableVisibleTabsNames: any = [];

  isDragging = false;

  @tuiPure
  stringify(
    items: readonly { id: string | number; title: string }[],
  ): TuiStringHandler<string | number> {
    const map = new Map(items.map(({ id, title }) => [id, title] as [string | number, string]));

    return (value: string | number) => map.get(value) ?? '';
  }

  drop(event: CdkDragDrop<string[]>, list: any) {
    moveItemInArray(list, event.previousIndex, event.currentIndex);
  }

  onAddressFormatDragStarted() {
    this.onDragStarted(this.addressFormatList);
  }

  onAddressFormatDragReleased() {
    this.onDragReleased(this.addressFormatList);
  }

  onObjectCardDragStarted() {
    this.onDragStarted(this.objectCardList);
  }

  onObjectCardDragReleased() {
    this.onDragReleased(this.objectCardList);
  }

  private onDragStarted(dropList?: ElementRef<HTMLElement>) {
    this.isDragging = true;
    dropList?.nativeElement.classList.add('drag-active');
  }

  private onDragReleased(dropList?: ElementRef<HTMLElement>) {
    dropList?.nativeElement.classList.remove('drag-active');
  }

  ngOnInit(): void {
    const addressFormat = this.user?.addressFormat || [];

    const objectCardSettings: Record<ObjectCardKind, ObjectCardSettingsType> = {
      floating: normalizeObjectCardSettings(this.user?.objectCardSettings),
      fixed: normalizeObjectCardSettings(this.user?.objectCardFixed),
    };

    this.availableVisibleTabsNames = monitoringTabs
      .filter((tab: any) => {
        if (['monitoring', 'objects', 'users'].includes(tab.name)) {
          return true;
        } else if (tab.name === 'tracks') {
          return (this.user?.commonAccesses || []).includes('access_tracks');
        } else if (tab.name === 'reports') {
          return (this.user?.commonAccesses || []).includes('access_reports');
        } else if (tab.name === 'messages') {
          return (this.user?.commonAccesses || []).includes('access_messages');
        } else if (tab.name === 'notifications') {
          return (this.user?.commonAccesses || []).includes('access_notifications');
        } else if (tab.name === 'geofences') {
          return (this.user?.commonAccesses || []).includes('access_geozones');
        } else if (tab.name === 'drivers') {
          return (this.user?.commonAccesses || []).includes('access_drivers');
        } else if (tab.name === 'trailers') {
          return (this.user?.commonAccesses || []).includes('access_trailers');
        } else if (tab.name === 'buildings') {
          return (this.user?.commonAccesses || []).includes('access_buildings');
        } else if (tab.name === 'devices') {
          return (this.user?.commonAccesses || []).includes('access_devices');
        } else if (tab.name === 'maintenance') {
          return (this.user?.commonAccesses || []).includes('access_maintenance');
        }
      })
      .map((m: any) => {
        return {
          ...m,
          control: new FormControl({
            value: this.formGroup.value.visibleTabsNames.includes(m.name),
            disabled: m?.id === 1,
          }),
        };
      });

    this.availableAddressFormat
      .sort(
        (a: { name: string; control: FormControl }, b: { name: string; control: FormControl }) => {
          const indexA = addressFormat.indexOf(a.name);
          const indexB = addressFormat.indexOf(b.name);
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        },
      )
      .forEach((item: { name: string; control: FormControl }) => {
        item.control.setValue(addressFormat.includes(item.name));
      });

    OBJECT_CARD_KINDS.forEach((kind) => {
      const generalContent = new Set(objectCardSettings[kind].general?.content ?? []);

      this.availableGeneralContent.forEach((field) => {
        field.controls[kind].setValue(generalContent.has(field.name));
      });

      this.availableSettingsObjectCard.forEach((setting) => {
        setting.controls[kind].setValue(setting.name in objectCardSettings[kind]);
      });
    });

    // Порядок общий, берём его из всплывающей карточки; блоки, выключенные в ней,
    // встают по порядку фиксированной, остальные — в конец
    const blockIndex = (name: string): number =>
      objectCardSettings.floating[name as ObjectCardBlockKey]?.index ??
      objectCardSettings.fixed[name as ObjectCardBlockKey]?.index ??
      Infinity;

    this.availableSettingsObjectCard.sort((a, b) => blockIndex(a.name) - blockIndex(b.name));
  }

  /** Собирает настройки карточки данного типа из состояния чекбоксов: `{ index, content }` */
  private buildObjectCardSettings(kind: ObjectCardKind): ObjectCardSettingsType {
    // general всегда на позиции 0 и не скрывается — настраивается только его содержимое
    return this.availableSettingsObjectCard
      .filter((item) => item.controls[kind].value)
      .reduce<ObjectCardSettingsType>(
        (acc, item, index) => {
          acc[item.name as ObjectCardBlockKey] = { index: index + 1, content: [] };
          return acc;
        },
        {
          general: {
            index: 0,
            content: this.availableGeneralContent
              .filter((field) => field.controls[kind].value)
              .map((field) => field.name),
          },
        },
      );
  }

  onSave() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const addressFormat = this.availableAddressFormat
      .filter((item: any) => item.control.value === true)
      .map((item: any) => item.name);

    const { backgroundOpacity, ...profile } = this.formGroup.value;

    const sensors = this.sensorsSettingsForm.getRawValue();

    const body = {
      ...profile,
      sensorPrecisionConfig: sensors,
      objectCardSettings: this.buildObjectCardSettings('floating'),
      // в CMS фиксированная карточка не настраивается — прокидываем сохранённое, чтобы не затереть
      objectCardFixed: this.currentProjectIsMonitoring
        ? this.buildObjectCardSettings('fixed')
        : normalizeObjectCardSettings(this.user?.objectCardFixed),
      mapObjectSettings: { backgroundOpacity: normalizeBackgroundOpacity(backgroundOpacity) },
    };

    if (this.currentProjectIsMonitoring) {
      body.visibleTabsNames = this.availableVisibleTabsNames
        .filter((item: any) => item.control.value)
        .map((item: any) => item.name);
    }

    if (addressFormat.length) {
      body.addressFormat = addressFormat;
    }

    this.userService
      .updateUserProfile(body)
      .pipe(
        tap(() => {
          if (environment.systemType === 'cms') {
            this.store.dispatch(getCurrentUser());
          }
        }),
        tap(() => this.context.completeWith(true)),
        concatMap(() =>
          this.alerts.open(this.translate.instant('Information updated successfully')),
        ),
      )
      .subscribe({
        next: () => {
          // успех обрабатывать не нужно, важна только ветка ошибки
        },
        error: (error: any) => {
          this.errorHandler.simpleErrorHandler(error, this.formGroup);
        },
      });
  }

  onCancel() {
    this.context.completeWith(false);
  }

  openChangePasswordDialog() {
    this.dialogService
      .open<number>(new PolymorpheusComponent(ChangePasswordComponent, this.injector), {
        label: this.translate.instant('Change password'),
        size: 's',
        dismissible: false,
      })
      .subscribe();
  }
}
